import { useId, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Banner, Button, Card, Dialog, IconButton, List, ListItem, Select, Skeleton, StatusChip, TextField, TopAppBar,
  useSnackbar,
} from '@meshble/molo';
import { api, must, type components, type Instance } from '../lib/api';
import { BarraUtente } from '../shell';
import { Back, Blueprint as IconaBlueprint, Copy, Info, Pacchetto, Plus, Trash } from '../icons';
import { Anello, CaricoSezione } from '../brand';
import { elenco } from './moduli';
import { useCatalogo } from './catalogo';
import { ModuliDelCatalogo, ScegliDalCatalogo } from './blueprint-catalogo';
import { repoDelCatalogo } from '../lib/catalogo';
import { t } from '../lib/lingua';
import { ConPermesso } from '../permesso';
import { usePermessiSu } from '../session';

type Blueprint = components['schemas']['Blueprint'];
type EsitoBlueprint = components['schemas']['EsitoBlueprint'];

/**
 * I blueprint (blocco 32): pacchetti di moduli dell'organizzazione — standard
 * di Odoo, dei repository, librerie Python — da applicare in un colpo solo a
 * un'istanza accesa o a una che nasce.
 */

export function useBlueprint(slug: string, abilitata = true) {
  return useQuery({
    queryKey: ['blueprint', slug],
    enabled: abilitata,
    // Chi non può collegare repository riceve 403: il blueprint non lo vede, e
    // non serve riprovare.
    retry: false,
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/blueprints', { params: { path: { slug } } })),
  });
}

function riassunto(b: Blueprint): string {
  const moduli = (b.modules ?? []).length + (b.repos ?? []).reduce((n, r) => n + (r.modules ?? []).length, 0);
  const repo = (b.repos ?? []).length;
  const librerie = (b.pythonPackages ?? []).length;
  return [
    moduli === 1 ? t('1 modulo') : t('{n} moduli', { n: moduli }),
    repo === 0 ? null : repo === 1 ? t('1 repository') : t('{n} repository', { n: repo }),
    librerie === 0 ? null : librerie === 1 ? t('1 libreria') : t('{n} librerie', { n: librerie }),
  ].filter(Boolean).join(' · ');
}

export function BlueprintPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/blueprint' });
  const navigate = useNavigate();
  const q = useBlueprint(slug);
  const tutti = q.data?.blueprints ?? [];
  const nuovo = () => navigate({ to: '/orgs/$slug/blueprint/$id', params: { slug, id: 'nuovo' } });

  return (
    <>
      <TopAppBar
        title={t('Blueprint')}
        actions={
          <>
            <Button variant="filled" icon={<Plus />} onClick={nuovo}>{t('Nuovo blueprint')}</Button>
            <BarraUtente slug={slug} />
          </>
        }
      />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-5)' }}>
          <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('Un blueprint è un pacchetto di moduli con un nome: quelli standard di Odoo, quelli dei vostri repository e di OCA, le librerie Python che servono. Si applica a un’istanza accesa, dalla sua scheda, o a una nuova, alla creazione: un solo lavoro, con una copia del database prima.')}
          </p>
          {q.error && <Banner tone="error" title={t('Non riesco a leggere i blueprint')}>{q.error.message}</Banner>}
          {q.isPending && <Skeleton height="8rem" />}
          {q.isSuccess && tutti.length === 0 && (
            <Card variant="outlined">
              <div className="md-stack">
                <p className="md-body-medium" style={{ margin: 0 }}>
                  {t('Nessun blueprint ancora. Il primo di solito è quello che installate su ogni cliente.')}
                </p>
                <div className="md-stack__inline">
                  <Button variant="tonal" icon={<Plus />} onClick={nuovo}>{t('Crea il primo blueprint')}</Button>
                </div>
              </div>
            </Card>
          )}
          {tutti.length > 0 && (
            <List>
              {tutti.map((b) => (
                <ListItem
                  key={b.id}
                  interactive
                  icon={<IconaBlueprint />}
                  headline={b.name}
                  supporting={[b.description, riassunto(b)].filter(Boolean).join(' · ')}
                  onClick={() => navigate({ to: '/orgs/$slug/blueprint/$id', params: { slug, id: b.id } })}
                />
              ))}
            </List>
          )}
        </div>
      </div>
    </>
  );
}

type RepoInModifica = { url: string; branch: string; moduli: string };

const MODULO = /^[A-Za-z0-9_]+$/;

function daBlueprint(b?: Blueprint) {
  return {
    nome: b?.name ?? '',
    descrizione: b?.description ?? '',
    moduli: (b?.modules ?? []).join(' '),
    librerie: (b?.pythonPackages ?? []).join('\n'),
    repos: (b?.repos ?? []).map((r) => ({ url: r.url, branch: r.branch ?? '', moduli: (r.modules ?? []).join(' ') })),
  };
}

export function BlueprintEditPage() {
  const { slug, id } = useParams({ from: '/shell/orgs/$slug/blueprint/$id' });
  const q = useBlueprint(slug);
  const esistente = (q.data?.blueprints ?? []).find((b) => b.id === id);
  const nuovo = id === 'nuovo';

  return (
    <>
      <TopAppBar
        leading={<TornaAiBlueprint slug={slug} />}
        title={nuovo ? t('Nuovo blueprint') : esistente?.name ?? t('Blueprint')}
        actions={<BarraUtente slug={slug} />}
      />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        {q.error && <Banner tone="error" title={t('Non riesco a leggere i blueprint')}>{q.error.message}</Banner>}
        {q.isPending && <Skeleton height="16rem" />}
        {q.isSuccess && !nuovo && !esistente && (
          <Banner tone="error" title={t('Questo blueprint non c’è più')}>
            <Link to="/orgs/$slug/blueprint" params={{ slug }}>{t('Torna ai blueprint')}</Link>
          </Banner>
        )}
        {q.isSuccess && (nuovo || esistente) && (
          // La chiave rimonta il modulo quando l'hub risponde con la versione
          // salvata: si riparte da quella, con le chiavi dei privati.
          <Modulo key={esistente ? `${esistente.id}:${esistente.updatedAt}` : 'nuovo'} slug={slug} blueprint={esistente} />
        )}
      </div>
    </>
  );
}

function TornaAiBlueprint({ slug }: { slug: string }) {
  const navigate = useNavigate();
  return (
    <IconButton label={t('Torna ai blueprint')} onClick={() => navigate({ to: '/orgs/$slug/blueprint', params: { slug } })}>
      <Back />
    </IconButton>
  );
}

function Modulo({ slug, blueprint }: { slug: string; blueprint?: Blueprint }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const avvisa = useSnackbar();
  const librerieId = useId();
  const [v, setV] = useState(() => daBlueprint(blueprint));
  const [elimina, setElimina] = useState(false);
  const [dalCatalogo, setDalCatalogo] = useState(false);
  // Il catalogo dice quali repository hanno rami e moduli da scegliere; per
  // gli altri — GitLab, fuori catalogo — restano i campi da scrivere.
  const cat = useCatalogo(slug);
  const delCatalogo = cat.data?.repos ?? [];

  const cambiaRepo = (i: number, r: Partial<RepoInModifica>) =>
    setV((p) => ({ ...p, repos: p.repos.map((x, j) => (j === i ? { ...x, ...r } : x)) }));

  const moduli = elenco(v.moduli);
  const sbagliati = [...moduli, ...v.repos.flatMap((r) => elenco(r.moduli))].filter((m) => !MODULO.test(m));
  const corpo = {
    name: v.nome.trim(),
    description: v.descrizione.trim(),
    modules: moduli,
    pythonPackages: elenco(v.librerie),
    repos: v.repos
      .filter((r) => r.url.trim())
      .map((r) => ({ url: r.url.trim(), modules: elenco(r.moduli), ...(r.branch.trim() ? { branch: r.branch.trim() } : {}) })),
  };
  const pronto = corpo.name !== '' && sbagliati.length === 0 &&
    (corpo.modules.length > 0 || corpo.repos.length > 0 || corpo.pythonPackages.length > 0) &&
    corpo.repos.every((r) => r.modules.length > 0);

  const salva = useMutation({
    mutationFn: () =>
      blueprint
        ? must(api.PUT('/api/v1/orgs/{slug}/blueprints/{id}', { params: { path: { slug, id: blueprint.id } }, body: corpo }))
        : must(api.POST('/api/v1/orgs/{slug}/blueprints', { params: { path: { slug } }, body: corpo })),
    onSuccess: (b) => {
      qc.invalidateQueries({ queryKey: ['blueprint', slug] });
      avvisa(t('Blueprint salvato'));
      if (!blueprint) navigate({ to: '/orgs/$slug/blueprint/$id', params: { slug, id: b.id } });
    },
  });
  const via = useMutation({
    mutationFn: () =>
      must(api.DELETE('/api/v1/orgs/{slug}/blueprints/{id}', { params: { path: { slug, id: blueprint?.id ?? '' } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['blueprint', slug] });
      avvisa(t('Blueprint eliminato'));
      navigate({ to: '/orgs/$slug/blueprint', params: { slug } });
    },
  });

  const privati = (blueprint?.repos ?? []).filter((r) => !r.public && r.publicKey);

  return (
    <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-5)' }}>
      {privati.map((r) => (
        <ChiaveDaIncollare key={r.url} repo={r.url} chiave={r.publicKey ?? ''} />
      ))}

      <Card variant="outlined">
        <div className="md-form">
          <TextField label={t('Nome')} value={v.nome} maxLength={80} onChange={(e) => setV({ ...v, nome: e.target.value })}
            help={t('Come lo cercherà chi crea un’istanza, es. «Base cliente» o «E-commerce».')} />
          <TextField label={t('Descrizione')} value={v.descrizione} maxLength={500}
            onChange={(e) => setV({ ...v, descrizione: e.target.value })} />
          <TextField
            label={t('Moduli standard di Odoo')}
            placeholder="sale_management, stock, account_accountant"
            spellCheck={false}
            value={v.moduli}
            onChange={(e) => setV({ ...v, moduli: e.target.value })}
            help={t('Nomi tecnici, separati da spazi o virgole: quelli che Odoo ha già, e con Enterprise anche i suoi.')}
          />
          <div className="md-field">
            <label htmlFor={librerieId}>{t('Librerie Python, una per riga')}</label>
            <textarea id={librerieId} rows={3} spellCheck={false} placeholder={'phonenumbers\npython-barcode==0.15.1'}
              value={v.librerie} onChange={(e) => setV({ ...v, librerie: e.target.value })} />
            <span className="md-field__help">
              {t('Si aggiungono a quelle dell’istanza, non le sostituiscono.')}
            </span>
          </div>
        </div>
      </Card>

      <Card variant="outlined">
        <div className="md-section-head">
          <h2 className="md-title-medium">{t('Repository')}</h2>
          <span className="md-stack__inline">
            <Button variant="text" icon={<Plus />} onClick={() => setV({ ...v, repos: [...v.repos, { url: '', branch: '', moduli: '' }] })}>
              {t('Aggiungi per indirizzo')}
            </Button>
            <Button variant="tonal" icon={<Pacchetto />} onClick={() => setDalCatalogo(true)}>
              {t('Aggiungi dal catalogo')}
            </Button>
          </span>
        </div>
        {v.repos.length === 0 && (
          <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('Nessuno: il blueprint installa solo moduli standard. Un repository di OCA o vostro porta i suoi moduli, sul ramo della versione dell’istanza.')}
          </p>
        )}
        <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-5)' }}>
          {v.repos.map((r, i) => {
            const nelCatalogo = repoDelCatalogo(delCatalogo, r.url);
            return (
            <div key={i} className="md-form">
              <div className="md-section-head">
                <h3 className="md-title-small" style={{ margin: 0 }}>{r.url.trim() || t('Nuovo repository')}</h3>
                <IconButton label={t('Togli il repository')} onClick={() => setV({ ...v, repos: v.repos.filter((_, j) => j !== i) })}>
                  <Trash />
                </IconButton>
              </div>
              <TextField
                label={t('Indirizzo')}
                placeholder="https://github.com/OCA/web"
                spellCheck={false}
                value={r.url}
                onChange={(e) => cambiaRepo(i, { url: e.target.value })}
              />
              {nelCatalogo ? (
                <ModuliDelCatalogo
                  slug={slug}
                  repo={nelCatalogo}
                  ramo={r.branch}
                  onRamo={(branch) => cambiaRepo(i, { branch })}
                  scelti={elenco(r.moduli)}
                  onScelti={(m) => cambiaRepo(i, { moduli: m.join(' ') })}
                />
              ) : (
                <>
                  <TextField
                    label={t('Ramo')}
                    placeholder={t('la versione dell’istanza, es. 18.0')}
                    spellCheck={false}
                    value={r.branch}
                    onChange={(e) => cambiaRepo(i, { branch: e.target.value })}
                    help={t('Vuoto: il ramo della versione di Odoo dell’istanza, così lo stesso blueprint va su una 17 e su una 18.')}
                  />
                  <TextField
                    label={t('Moduli da installare')}
                    placeholder="web_responsive, web_dialog_size"
                    spellCheck={false}
                    value={r.moduli}
                    error={r.url.trim() !== '' && elenco(r.moduli).length === 0}
                    onChange={(e) => cambiaRepo(i, { moduli: e.target.value })}
                    help={t('Almeno uno. Uno che nel ramo non c’è si dice quando lo applichi, e gli altri si installano lo stesso.')}
                  />
                </>
              )}
            </div>
            );
          })}
        </div>
      </Card>

      {sbagliati.length > 0 && (
        <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-error)' }}>
          {t('Non è un nome di modulo: {nomi}. Solo lettere, cifre e underscore.', { nomi: sbagliati.join(', ') })}
        </p>
      )}
      {salva.error && <Banner tone="error" title={t('Non l’ho salvato')}>{(salva.error as Error).message}</Banner>}
      {via.error && <Banner tone="error" title={t('Non l’ho eliminato')}>{(via.error as Error).message}</Banner>}
      <div className="md-stack__inline">
        <Button variant="filled" icon={salva.isPending ? <Anello /> : undefined} disabled={!pronto || salva.isPending}
          onClick={() => salva.mutate()}>
          {salva.isPending ? t('Salvo…') : t('Salva')}
        </Button>
        {blueprint && (
          <Button variant="text" icon={<Trash />} onClick={() => setElimina(true)}>{t('Elimina')}</Button>
        )}
      </div>

      <ScegliDalCatalogo
        open={dalCatalogo}
        onOpenChange={setDalCatalogo}
        // Quelli già nel blueprint non si offrono due volte.
        repos={delCatalogo.filter((c) => !v.repos.some((r) => repoDelCatalogo([c], r.url)))}
        errore={cat.error}
        onScelto={(c) => {
          setV((p) => ({ ...p, repos: [...p.repos, { url: c.url, branch: '', moduli: '' }] }));
          setDalCatalogo(false);
        }}
      />

      <Dialog
        open={elimina}
        onOpenChange={setElimina}
        title={t('Eliminare {nome}?', { nome: blueprint?.name ?? '' })}
        description={t('Le istanze su cui l’hai applicato restano come sono: il blueprint era la ricetta, non un legame.')}
        actions={
          <>
            <Button variant="text" onClick={() => setElimina(false)}>{t('Annulla')}</Button>
            <Button variant="filled" icon={via.isPending ? <Anello /> : undefined} disabled={via.isPending} onClick={() => via.mutate()}>
              {t('Elimina')}
            </Button>
          </>
        }
      />
    </div>
  );
}

function ChiaveDaIncollare({ repo, chiave }: { repo: string; chiave: string }) {
  const avvisa = useSnackbar();
  return (
    <Banner
      icon={<Info />}
      title={t('Chiave di deploy per {repo}', { repo })}
      action={
        <Button
          variant="text"
          icon={<Copy />}
          onClick={() => {
            void navigator.clipboard?.writeText(chiave);
            avvisa(t('Chiave copiata'));
          }}
        >
          {t('Copia')}
        </Button>
      }
    >
      <div className="md-stack">
        <p style={{ margin: 0 }}>
          {t('Il repository è privato: arriva sul server con la chiave dell’azienda. Se non l’hai già fatto, su GitHub: «Settings → Deploy keys → Add deploy key», {senza} spuntare «Allow write access».', {
            senza: <b>{t('senza')}</b>,
          })}
        </p>
        <code className="md-mono" style={{ display: 'block', overflowX: 'auto', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
          {chiave}
        </code>
      </div>
    </Banner>
  );
}

/**
 * «Blueprint…» nella scheda dell'istanza: sceglierne uno e applicarlo. Un
 * job solo, con la copia del database prima; quello che nel ramo manca si
 * dice qui, prima di andare a seguire il lavoro.
 */
export function ApplicaBlueprint({ slug, istanza }: { slug: string; istanza: Instance }) {
  const permessi = usePermessiSu(slug, { istanza: istanza.id });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const avvisa = useSnackbar();
  const [apri, setApri] = useState(false);
  const [scelto, setScelto] = useState('');
  const [esito, setEsito] = useState<EsitoBlueprint | null>(null);
  const q = useBlueprint(slug);
  const tutti = q.data?.blueprints ?? [];
  const bp = tutti.find((b) => b.id === scelto) ?? tutti[0];

  const applica = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/instances/{id}/blueprint', {
        params: { path: { slug, id: istanza.id } },
        body: { blueprintId: bp?.id ?? '' },
      })),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['instance', slug, istanza.id] });
      qc.invalidateQueries({ queryKey: ['addons', slug, istanza.id] });
      const daDire = r.publicKey || (r.missing ?? []).length > 0 || (r.skipped ?? []).length > 0;
      if (r.jobId && !daDire) {
        setApri(false);
        avvisa(t('Applico {nome}: l’istanza si ferma un momento', { nome: bp?.name ?? '' }));
        navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: r.jobId } });
        return;
      }
      setEsito(r);
    },
  });

  // Chi non vede i blueprint (403) non ha il bottone.
  if (q.isError) return null;

  return (
    <>
      {/* Anche da «Fallita»: è dove resta un'istanza se il blueprint si ferma a metà. */}
      <ConPermesso ha={permessi} serve={['instance.deploy', 'module.install']}>
        <Button variant="text" disabled={istanza.status !== 'running' && istanza.status !== 'error'}
          onClick={() => { setEsito(null); setApri(true); }}>
          {t('Blueprint…')}
        </Button>
      </ConPermesso>
      <Dialog
        open={apri}
        onOpenChange={(v) => !v && setApri(false)}
        title={t('Applica un blueprint')}
        description={t('Collego i repository che mancano, aggiungo le librerie e installo i moduli, in un solo lavoro con una copia del database prima. I moduli già installati li salto.')}
        actions={
          <>
            <Button variant="text" onClick={() => setApri(false)}>{esito ? t('Chiudi') : t('Annulla')}</Button>
            {!esito?.jobId && (
              <Button variant="filled" icon={applica.isPending ? <Anello /> : undefined} disabled={!bp || applica.isPending}
                onClick={() => applica.mutate()}>
                {applica.isPending ? t('Avvio…') : esito?.publicKey ? t('Ho incollato, applica') : t('Applica')}
              </Button>
            )}
          </>
        }
      >
        <div className="md-stack">
          {applica.error && <Banner tone="error" title={t('Non partito')}>{(applica.error as Error).message}</Banner>}
          {q.isPending && <Skeleton height="3rem" />}
          {q.isSuccess && tutti.length === 0 && (
            <p className="md-body-medium" style={{ margin: 0 }}>
              {t('Nessun blueprint ancora: li crei in {pagina}.', {
                pagina: <Link to="/orgs/$slug/blueprint" params={{ slug }}>{t('Blueprint')}</Link>,
              })}
            </p>
          )}
          {tutti.length > 0 && !esito?.jobId && (
            <Select
              label={t('Blueprint|campo')}
              value={bp?.id ?? ''}
              options={tutti.map((b) => ({ value: b.id, label: b.name }))}
              onChange={(e) => { setScelto(e.target.value); setEsito(null); }}
              help={bp ? riassunto(bp) : undefined}
            />
          )}
          {esito?.publicKey && <ChiaveDaIncollare repo={esito.keyRepo ?? ''} chiave={esito.publicKey} />}
          {esito && !esito.publicKey && (
            <List>
              {(esito.install ?? []).length > 0 && <ListItem headline={t('Installo')} supporting={(esito.install ?? []).join(', ')} />}
              {(esito.alreadyInstalled ?? []).length > 0 && (
                <ListItem headline={t('Già installati')} supporting={(esito.alreadyInstalled ?? []).join(', ')} />
              )}
              {(esito.missing ?? []).length > 0 && (
                <ListItem headline={t('Non nel ramo')} supporting={(esito.missing ?? []).join(', ')}
                  trailing={<StatusChip status="warning" label={t('Saltati')} tone="tertiary" />} />
              )}
              {(esito.skipped ?? []).length > 0 && (
                <ListItem headline={t('Repository lasciati fuori')} supporting={(esito.skipped ?? []).join(' · ')} />
              )}
              <ListItem
                headline={esito.jobId ? t('Partito') : t('Niente da fare: è già tutto installato')}
                trailing={esito.jobId ? <Link to="/orgs/$slug/jobs/$id" params={{ slug, id: esito.jobId }}>{t('Segui')}</Link> : undefined}
              />
            </List>
          )}
        </div>
      </Dialog>
    </>
  );
}
